import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useComfort } from "../ComfortContext";
import { fill, getDictionary, type Locale, type PortalDictionary } from "../../i18n/dictionary";
import { NOTE_MAX, SUGGESTION_GAMES_MAX } from "../../domain/assignmentNote";
import { DOMAIN_ORDER, localized } from "../../progress/registry";
import { bedLabel } from "../../progress/text";
import {
  getLinkedChildren,
  FIXTURE_ACCOUNT_ID,
  PLAY_TARGETS,
  getChildAssignments,
} from "./data";
import type { LinkedChild, PlayTarget } from "./data";
import { getPortalData, notifyFamilyInBackground, shouldUseSupabase } from "../../data";
import { PortalWriteError } from "../../data/supabaseAdapter";
import { useLinkedChildren } from "./useLinkedChildren";
import { LoadingChildren, ChildrenLoadError } from "./LoadState";
import type { ServiceOutletContext } from "./ServiceSettings";

type WizardStep = 1 | 2 | 3;

// ---- Shared helpers ----------------------------------------------------------

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: ".12em",
        textTransform: "uppercase",
        color: "var(--ink-faint)",
        margin: "0 0 12px",
      }}
    >
      {children}
    </h2>
  );
}

function BackButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        background: "transparent",
        border: 0,
        color: "var(--ink-soft)",
        fontFamily: "var(--font-body)",
        fontWeight: 600,
        fontSize: 14,
        cursor: "pointer",
        padding: "6px 0",
        marginBottom: 20,
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ transform: "scaleX(-1)" }}
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
      {label}
    </button>
  );
}

// ---- Step indicator ----------------------------------------------------------

function StepIndicator({
  step,
  labels,
}: {
  step: WizardStep;
  labels: [string, string, string];
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0,
        marginBottom: 28,
      }}
    >
      {labels.map((label, i) => {
        const n = (i + 1) as WizardStep;
        const active = n === step;
        const done = n < step;
        return (
          <div key={n} style={{ display: "flex", alignItems: "flex-start" }}>
            <div style={{ textAlign: "center" }}>
              <span
                style={{
                  display: "inline-flex",
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: active || done ? "var(--brand)" : "var(--surface-sunk)",
                  color: active || done ? "#fff" : "var(--ink-faint)",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {done ? "✓" : n}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: 11.5,
                  color: active ? "var(--brand)" : "var(--ink-faint)",
                  marginTop: 4,
                  maxWidth: 72,
                  lineHeight: 1.35,
                }}
              >
                {label}
              </span>
            </div>
            {n < 3 && (
              <div
                style={{
                  width: 36,
                  height: 1.5,
                  background: done ? "var(--brand)" : "var(--line)",
                  margin: "13px 6px 0",
                  flex: "none",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---- Step 1: Pick a child ----------------------------------------------------

function PickChild({
  children: activeChildren,
  selected,
  onSelect,
}: {
  children: LinkedChild[];
  selected: LinkedChild | null;
  onSelect: (lc: LinkedChild) => void;
}) {
  if (activeChildren.length === 0) {
    return (
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          padding: "24px 22px",
          color: "var(--ink-soft)",
          fontSize: 15.5,
        }}
      >
        No children are currently linked with active access. Link a child first.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {activeChildren.map((lc) => {
        const isSelected = selected?.child.id === lc.child.id;
        return (
          <button
            key={lc.child.id}
            onClick={() => onSelect(lc)}
            aria-pressed={isSelected}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "14px 18px",
              background: isSelected ? "var(--brand-tint)" : "var(--surface)",
              border: `1.5px solid ${isSelected ? "var(--brand)" : "var(--line)"}`,
              borderRadius: "var(--r-lg)",
              cursor: "pointer",
              textAlign: "left",
              minHeight: 64,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: isSelected ? "var(--brand)" : "var(--brand-tint)",
                display: "grid",
                placeItems: "center",
                flex: "none",
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: 16,
                color: isSelected ? "#fff" : "var(--brand)",
              }}
            >
              {lc.child.displayLabel.charAt(0)}
            </span>
            <div>
              <b
                style={{
                  display: "block",
                  fontFamily: "var(--font-body)",
                  fontSize: 15.5,
                  color: "var(--ink-deep)",
                }}
              >
                {lc.child.displayLabel}
              </b>
              <span style={{ fontSize: 13, color: "var(--ink-faint)" }}>
                {lc.link.relation === "owner" ? "You own this" : "Shared with you"}
              </span>
            </div>
            {isSelected && (
              <span
                aria-hidden="true"
                style={{
                  marginLeft: "auto",
                  color: "var(--brand)",
                  fontWeight: 700,
                  fontSize: 18,
                }}
              >
                ✓
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---- Step 2: Choose games ----------------------------------------------------
//
// Only the bundled games, grouped by area with the same area names the family's
// Skill Garden uses. Up to three may be chosen together (owner, 2026-10-23): a
// short, focused set is what a family can follow at home. At three the rest are
// disabled, with a calm line saying why and how to swap.

function ChooseTargets({
  selected,
  onToggle,
  locale,
  dict,
  premiumLocked,
}: {
  selected: readonly PlayTarget[];
  onToggle: (t: PlayTarget) => void;
  locale: Locale;
  dict: PortalDictionary;
  /** The child's family has no Premium (child_access): Premium games can't be chosen. */
  premiumLocked: boolean;
}) {
  const p = dict.service.progress;
  const ta = dict.service.assign;
  const atMax = selected.length >= SUGGESTION_GAMES_MAX;
  const domains = Array.from(new Set(PLAY_TARGETS.map((t) => t.domain))).sort(
    (a, b) =>
      (DOMAIN_ORDER as readonly string[]).indexOf(a) - (DOMAIN_ORDER as readonly string[]).indexOf(b),
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: "var(--bg, var(--surface))",
          paddingBlock: 8,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <span
          id="assign-chosen-count"
          aria-live="polite"
          style={{ fontSize: 14.5, fontWeight: 700, color: "var(--ink-deep)" }}
        >
          {fill(ta.chosenCount, { n: selected.length, max: SUGGESTION_GAMES_MAX })}
        </span>
        {atMax && (
          <span id="assign-max-hint" role="status" style={{ fontSize: 13.5, color: "var(--ink-soft)", lineHeight: 1.5 }}>
            {ta.maxReached}
          </span>
        )}
        {premiumLocked && (
          <span id="assign-premium-hint" role="note" style={{ fontSize: 13.5, color: "var(--ink-soft)", lineHeight: 1.5 }}>
            {ta.premiumLocked}
          </span>
        )}
      </div>
      {domains.map((domain) => {
        const targets = PLAY_TARGETS.filter((t) => t.domain === domain);
        const headingId = `assign-area-${domain.replace(/[^a-z0-9]+/gi, "-")}`;
        return (
          <section key={domain} aria-labelledby={headingId}>
            <SectionHeading>
              <span id={headingId}>{bedLabel(p, domain)}</span>
            </SectionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {targets.map((target) => {
                const isSelected = selected.some((s) => s.id === target.id);
                const locked = premiumLocked && !target.free;
                const disabled = locked || (atMax && !isSelected);
                const inputId = `assign-game-${target.id}`;
                const describedBy = locked ? "assign-premium-hint" : disabled ? "assign-max-hint" : undefined;
                return (
                  <label
                    key={target.id}
                    htmlFor={inputId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 18px",
                      background: isSelected ? "var(--brand-tint)" : "var(--surface)",
                      border: `1.5px solid ${isSelected ? "var(--brand)" : "var(--line)"}`,
                      borderRadius: "var(--r-lg)",
                      cursor: disabled ? "default" : "pointer",
                      opacity: disabled ? 0.55 : 1,
                      minHeight: 48,
                    }}
                  >
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={isSelected}
                      disabled={disabled}
                      aria-describedby={describedBy}
                      onChange={() => onToggle(target)}
                      style={{ width: 20, height: 20, flex: "none", accentColor: "var(--brand)", margin: 0 }}
                    />
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 15,
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected ? "var(--brand)" : "var(--ink)",
                      }}
                    >
                      {localized(target.name, locale)}
                    </span>
                    {locked && (
                      <span
                        style={{
                          flex: "none",
                          fontSize: 12,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: "var(--r-pill)",
                          background: "var(--surface-sunk)",
                          color: "var(--ink-soft)",
                          border: "1px solid var(--line)",
                        }}
                      >
                        {ta.premiumTag}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ---- Step 3: Confirm ---------------------------------------------------------

const eyebrow = {
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: ".1em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
} as const;

function ConfirmStep({
  child,
  targets,
  existingTargets,
  note,
  onNoteChange,
  locale,
  dict,
  submitting,
  onBack,
  onSubmit,
}: {
  child: LinkedChild;
  targets: readonly PlayTarget[];
  /**
   * Target ids already sent to this child. Passed in rather than looked up
   * here: the fixture store never knows a live child UUID, so a lookup here
   * would never find anything on the deployed site.
   */
  existingTargets: readonly string[];
  note: string;
  onNoteChange: (v: string) => void;
  locale: Locale;
  dict: PortalDictionary;
  submitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const ta = dict.service.assign;
  const noteLen = Array.from(note).length;
  const already = targets.filter((t) => existingTargets.includes(t.id));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          padding: "20px 22px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div>
          <span style={eyebrow}>{ta.confirmFor}</span>
          <p style={{ fontSize: 17, fontWeight: 600, color: "var(--ink-deep)", margin: "4px 0 0" }}>
            {child.child.displayLabel}
          </p>
        </div>
        <div style={{ height: 1, background: "var(--line)" }} />
        <div>
          <span id="assign-confirm-games" style={eyebrow}>
            {ta.confirmGames}
          </span>
          <ul aria-labelledby="assign-confirm-games" style={{ listStyle: "none", margin: "6px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
            {targets.map((target) => (
              <li key={target.id}>
                <b style={{ display: "block", fontSize: 16.5, fontWeight: 600, color: "var(--ink-deep)" }}>
                  {localized(target.name, locale)}
                </b>
                <span style={{ fontSize: 13, color: "var(--ink-faint)" }}>
                  {bedLabel(dict.service.progress, target.domain)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* One note for the whole set: the family sees it in their app. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <label htmlFor="assign-note" style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink-deep)" }}>
          {ta.noteLabel}
        </label>
        <span id="assign-note-hint" style={{ fontSize: 13, color: "var(--ink-soft)" }}>
          {ta.noteHint} {ta.notePrivacy}
        </span>
        <textarea
          id="assign-note"
          aria-describedby="assign-note-hint assign-note-count"
          value={note}
          maxLength={NOTE_MAX}
          rows={3}
          onChange={(e) => onNoteChange(Array.from(e.target.value).slice(0, NOTE_MAX).join(""))}
          style={{
            width: "100%",
            boxSizing: "border-box",
            minHeight: 88,
            padding: "10px 14px",
            borderRadius: "var(--r-md)",
            border: "1.5px solid var(--line-strong)",
            background: "var(--surface)",
            color: "var(--ink)",
            fontFamily: "var(--font-body)",
            fontSize: 15,
            lineHeight: 1.5,
            resize: "vertical",
          }}
        />
        <span id="assign-note-count" aria-live="polite" style={{ fontSize: 12.5, color: "var(--ink-faint)", alignSelf: "flex-end" }}>
          {fill(ta.noteCount, { n: noteLen, max: NOTE_MAX })}
        </span>
      </div>

      {already.length > 0 && (
        <div
          style={{
            background: "var(--peach-tint)",
            border: "1px solid var(--peach)",
            borderRadius: "var(--r-lg)",
            padding: "14px 18px",
            fontSize: 14,
            color: "var(--peach-deep)",
          }}
        >
          {fill(ta.alreadySent, { games: already.map((t) => localized(t.name, locale)).join(", ") })}
        </div>
      )}

      <p style={{ fontSize: 14, color: "var(--ink-soft)", margin: 0, lineHeight: 1.6 }}>{ta.confirmBody}</p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            minHeight: 48,
            padding: "0 20px",
            border: "1.5px solid var(--line)",
            background: "transparent",
            color: "var(--ink-soft)",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: 15,
            borderRadius: "var(--r-pill)",
            cursor: "pointer",
          }}
        >
          {ta.back}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          aria-busy={submitting}
          style={{
            minHeight: 48,
            padding: "0 24px",
            border: 0,
            background: "var(--brand)",
            color: "#fff",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: 15,
            borderRadius: "var(--r-pill)",
            cursor: submitting ? "default" : "pointer",
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? ta.sending : ta.sendButton}
        </button>
      </div>
    </div>
  );
}

// ---- Success state -----------------------------------------------------------

function SuccessState({
  child,
  targets,
  locale,
  dict,
  onAssignAnother,
  onViewChild,
}: {
  child: LinkedChild;
  targets: readonly PlayTarget[];
  locale: Locale;
  dict: PortalDictionary;
  onAssignAnother: () => void;
  onViewChild: () => void;
}) {
  const ta = dict.service.assign;
  return (
    <div
      role="status"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 20,
        padding: "32px 0",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "var(--good-tint)",
          display: "grid",
          placeItems: "center",
          fontSize: 28,
          color: "var(--good)",
        }}
      >
        ✓
      </span>
      <div>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 22,
            color: "var(--ink-deep)",
            margin: "0 0 8px",
          }}
        >
          {ta.sentTitle}
        </h2>
        <p style={{ color: "var(--ink-soft)", fontSize: 15.5, margin: 0, lineHeight: 1.6 }}>
          {fill(ta.sentBody, {
            games: targets.map((t) => localized(t.name, locale)).join(", "),
            child: child.child.displayLabel,
          })}
        </p>
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
        <button
          type="button"
          onClick={onAssignAnother}
          style={{
            minHeight: 48,
            padding: "0 20px",
            border: "1.5px solid var(--brand)",
            background: "transparent",
            color: "var(--brand)",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: 15,
            borderRadius: "var(--r-pill)",
            cursor: "pointer",
          }}
        >
          {ta.sendMore}
        </button>
        <button
          type="button"
          onClick={onViewChild}
          style={{
            minHeight: 48,
            padding: "0 20px",
            border: 0,
            background: "var(--brand)",
            color: "#fff",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: 15,
            borderRadius: "var(--r-pill)",
            cursor: "pointer",
          }}
        >
          {fill(ta.viewChild, { child: child.child.displayLabel.split(" ")[0] })}
        </button>
      </div>
    </div>
  );
}

// ---- Errors ------------------------------------------------------------------

/**
 * create_suggestions' refusals in calm words. 42501: the family has not shared
 * "results" with this adult. P0001 carries several rule violations, told apart
 * by their fixed SQL messages; anything else is the generic "try again".
 */
export function sendErrorMessage(err: unknown, ta: PortalDictionary["service"]["assign"]): string {
  if (!(err instanceof PortalWriteError)) return ta.sendError;
  if (err.code === "42501") return ta.needResults;
  if (err.code === "P0001") {
    if (/at least one game/i.test(err.message)) return ta.chooseOne;
    if (/up to 3 games/i.test(err.message)) return ta.tooMany;
    if (/premium game/i.test(err.message)) return ta.premiumLocked;
    if (/280|note/i.test(err.message)) return ta.noteTooLong;
  }
  return ta.sendError;
}

// ---- Main export -------------------------------------------------------------

interface WizardProps {
  activeChildren: LinkedChild[];
  /** Target ids already sent, per child id. */
  existingByChild: Readonly<Record<string, readonly string[]>>;
  onSubmit: (child: LinkedChild, targets: readonly PlayTarget[], note: string) => Promise<void> | void;
}

function AssignPracticeWizard({ activeChildren, existingByChild, onSubmit }: WizardProps) {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const t = dict.service.assign;

  // ?child=<id> (from a child page's Messages tab) starts on step 2 for that child.
  const preselected = useMemo(() => {
    const id = params.get("child");
    return id ? activeChildren.find((lc) => lc.child.id === id) ?? null : null;
  }, [params, activeChildren]);

  const [step, setStep] = useState<WizardStep>(preselected ? 2 : 1);
  const [selectedChild, setSelectedChild] = useState<LinkedChild | null>(preselected);
  const [selectedTargets, setSelectedTargets] = useState<PlayTarget[]>([]);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  // Premium for the chosen child's family (child_access). Unknown (still loading,
  // or the read failed) gates nothing here: create_suggestions refuses a Premium
  // game for a free family anyway, and that refusal is said in the same words.
  const [premiumByChild, setPremiumByChild] = useState<Record<string, boolean>>({});
  const childId = selectedChild?.child.id ?? null;
  useEffect(() => {
    if (!childId || childId in premiumByChild) return;
    let alive = true;
    getPortalData()
      .childAccess(childId)
      .then((a) => {
        if (alive) setPremiumByChild((cur) => ({ ...cur, [childId]: a.premium }));
      })
      .catch(() => {
        // Leave it unknown; the server still enforces the rule.
      });
    return () => {
      alive = false;
    };
  }, [childId, premiumByChild]);
  const premiumLocked = childId !== null && premiumByChild[childId] === false;

  // A Premium game chosen before the answer arrived (or for another child) is let go.
  useEffect(() => {
    if (premiumLocked) setSelectedTargets((cur) => (cur.every((x) => x.free) ? cur : cur.filter((x) => x.free)));
  }, [premiumLocked]);

  function reset() {
    setStep(1);
    setSelectedChild(null);
    setSelectedTargets([]);
    setNote("");
    setDone(false);
    setError(null);
  }

  function toggleTarget(target: PlayTarget) {
    if (premiumLocked && !target.free) return;
    setSelectedTargets((cur) => {
      if (cur.some((x) => x.id === target.id)) return cur.filter((x) => x.id !== target.id);
      if (cur.length >= SUGGESTION_GAMES_MAX) return cur;
      return [...cur, target];
    });
  }

  async function handleSubmit() {
    if (!selectedChild || selectedTargets.length === 0 || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(selectedChild, selectedTargets, note);
      setDone(true);
    } catch (err) {
      setError(sendErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  const nextButton = (onClick: () => void) => (
    <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          minHeight: 48,
          padding: "0 24px",
          border: 0,
          background: "var(--brand)",
          color: "#fff",
          fontFamily: "var(--font-body)",
          fontWeight: 600,
          fontSize: 15,
          borderRadius: "var(--r-pill)",
          cursor: "pointer",
        }}
      >
        {t.next}
      </button>
    </div>
  );

  if (done && selectedChild && selectedTargets.length > 0) {
    return (
      <div>
        <SuccessState
          child={selectedChild}
          targets={selectedTargets}
          locale={locale}
          dict={dict}
          onAssignAnother={reset}
          onViewChild={() => navigate(`/children/${selectedChild.child.id}?tab=messages`)}
        />
      </div>
    );
  }

  return (
    <div>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 28,
          color: "var(--ink-deep)",
          margin: "0 0 6px",
        }}
      >
        {t.title}
      </h1>
      <p style={{ color: "var(--ink-soft)", fontSize: 15.5, margin: "0 0 28px", lineHeight: 1.6 }}>
        {t.subtitle}
      </p>

      <StepIndicator step={step} labels={[t.step1, t.step2, t.step3]} />

      {step === 1 && (
        <div>
          <SectionHeading>{t.step1}</SectionHeading>
          <PickChild
            children={activeChildren}
            selected={selectedChild}
            onSelect={setSelectedChild}
          />
          {selectedChild && nextButton(() => setStep(2))}
        </div>
      )}

      {step === 2 && (
        <div>
          <BackButton label={t.back} onClick={() => setStep(1)} />
          <SectionHeading>{t.step2}</SectionHeading>
          <ChooseTargets
            selected={selectedTargets}
            onToggle={toggleTarget}
            locale={locale}
            dict={dict}
            premiumLocked={premiumLocked}
          />
          {selectedTargets.length > 0 && nextButton(() => setStep(3))}
        </div>
      )}

      {step === 3 && selectedChild && selectedTargets.length > 0 && (
        <div>
          <SectionHeading>{t.step3}</SectionHeading>
          {error && (
            <div role="alert" style={{ background: "var(--peach-tint)", border: "1px solid var(--peach)", borderRadius: "var(--r-md)", padding: "12px 16px", color: "var(--peach-deep)", fontSize: 14.5, marginBottom: 16 }}>
              {error}
            </div>
          )}
          <ConfirmStep
            child={selectedChild}
            targets={selectedTargets}
            existingTargets={existingByChild[selectedChild.child.id] ?? []}
            note={note}
            onNoteChange={setNote}
            locale={locale}
            dict={dict}
            submitting={submitting}
            onBack={() => setStep(2)}
            onSubmit={() => void handleSubmit()}
          />
        </div>
      )}
    </div>
  );
}

// ---- Dispatcher + demo/live wrappers -----------------------------------------

/** Real persistence when Supabase is configured, else the demo store. */
export function AssignPractice() {
  return shouldUseSupabase() ? <AssignPracticeLive /> : <AssignPracticeDemo />;
}

/**
 * Sends the set as ONE group (create_suggestions) and then ONE push for it.
 * The push is fire-and-forget: the ideas are already saved, so a push failure
 * never fails or delays the send and is never shown here (only logged). In the
 * demo the fixtures adapter simulates both, sharing one batch id.
 */
async function sendSuggestions(child: LinkedChild, targets: readonly PlayTarget[], note: string): Promise<void> {
  const batchId = await getPortalData().createSuggestions(
    child.child.id,
    targets.map((t) => t.id),
    note,
  );
  notifyFamilyInBackground({ kind: "suggestions", batchId });
}

function AssignPracticeDemo() {
  const activeChildren = getLinkedChildren(FIXTURE_ACCOUNT_ID).filter(
    (lc) => lc.linkState === "active",
  );
  const [sentByChild, setSentByChild] = useState<Record<string, string[]>>({});
  const existingByChild = Object.fromEntries(
    activeChildren.map((lc) => [
      lc.child.id,
      [...getChildAssignments(lc.child.id).map((a) => a.target), ...(sentByChild[lc.child.id] ?? [])],
    ]),
  );
  return (
    <AssignPracticeWizard
      activeChildren={activeChildren}
      existingByChild={existingByChild}
      onSubmit={async (child, targets, note) => {
        await sendSuggestions(child, targets, note);
        setSentByChild((cur) => ({
          ...cur,
          [child.child.id]: [...(cur[child.child.id] ?? []), ...targets.map((t) => t.id)],
        }));
      }}
    />
  );
}

function AssignPracticeLive() {
  const ctx = useOutletContext<ServiceOutletContext | null>();
  const { linked, loading, error, refresh } = useLinkedChildren(ctx?.userId);

  // Assignable = active links where the caller owns the child or holds the
  // "results" scope (what create_suggestions authorizes). useLinkedChildren
  // already resolves relation, consent, scope and the one client-side label.
  const assignable = useMemo(
    () =>
      linked.filter(
        (lc) =>
          lc.linkState === "active" &&
          (lc.link.relation === "owner" || lc.link.scope.includes("results")),
      ),
    [linked],
  );

  // Already-sent targets, read back from the server so the confirm step's
  // "sent before" notice is true live. RLS scopes `assignments()` to children
  // the caller may see, so no extra filtering is needed here.
  const [existingByChild, setExistingByChild] = useState<
    Record<string, readonly string[]>
  >({});
  const [assignTick, setAssignTick] = useState(0);
  const reloadAssignments = useCallback(
    () => setAssignTick((n) => n + 1),
    [],
  );

  useEffect(() => {
    let alive = true;
    getPortalData()
      .assignments()
      .then((rows) => {
        if (!alive) return;
        const byChild: Record<string, string[]> = {};
        for (const a of rows) {
          (byChild[a.childId] ??= []).push(a.target);
        }
        setExistingByChild(byChild);
      })
      .catch(() => {
        // A failure here only costs the "sent before" hint, so it must not
        // block sending. Leave the map empty and stay silent.
        if (alive) setExistingByChild({});
      });
    return () => {
      alive = false;
    };
  }, [assignTick]);

  if (loading) return <LoadingChildren label="Loading your children…" />;
  if (error) return <ChildrenLoadError message={error} onRetry={refresh} />;

  return (
    <AssignPracticeWizard
      activeChildren={assignable}
      existingByChild={existingByChild}
      onSubmit={async (child, targets, note) => {
        await sendSuggestions(child, targets, note);
        reloadAssignments();
      }}
    />
  );
}
